import moment from 'moment';

export interface GoogleAnalyticsData {
    value: number;
    date: string;
    category: string;
}

export interface NPS {
    value: number;
    date: string;
}

const parseAnalyticsDate = (value: string): moment.Moment => {
    const strictDate = moment(value, ['MMMM Do YYYY', 'YYYY-MM-DD'], true);
    return strictDate.isValid() ? strictDate : moment(value);
};

export const LINE_CONFIG = (data: NPS[]) => {
    return {
        data,
        height: 300,
        xField: 'date',
        yField: 'value',
        point: {
            size: 7,
            shape: 'circle',
        },
        label: {
            style: {
                fill: '#aaa',
            },
        },
    };
};

interface RechartsDataPoint {
    date: string;
    [key: string]: string | number | undefined;
}

/**
 * Reduces the number of chart data points by grouping daily values into
 * calendar-aligned buckets and averaging them.
 *
 * Granularity adapts to the date range:
 *   - < 60 days  → no downsampling (daily points)
 *   - 60–180 days → one point per ISO week (Mon–Sun average)
 *   - > 180 days  → one point per calendar month
 *
 * Each bucket produces a single point whose date is the start of the period
 * (e.g. the Monday of the week, or the 1st of the month) and whose numeric
 * values are the rounded average of all daily values in that period.
 *
 * All numeric keys on the data points (e.g. "Couche-Tard", "Circle K",
 * "Active Users") are averaged independently so multi-line charts stay
 * aligned on the same dates.
 */
type DownsampleUnit = 'day' | 'isoWeek' | 'month';

const downsampleByPeriod = (
    data: RechartsDataPoint[]
): { data: RechartsDataPoint[]; unit: DownsampleUnit } => {
    if (data.length < 2) return { data, unit: 'day' };

    const first = parseAnalyticsDate(data[0].date);
    const last = parseAnalyticsDate(data[data.length - 1].date);
    const spanDays = last.diff(first, 'days');

    if (spanDays < 60) return { data, unit: 'day' };

    const unit = spanDays <= 180 ? 'isoWeek' : 'month';

    // Group daily points into calendar-aligned buckets
    const buckets: Record<string, RechartsDataPoint[]> = {};
    for (const point of data) {
        const key = parseAnalyticsDate(point.date).startOf(unit).format();
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(point);
    }

    // Average each bucket into a single data point
    const averaged = Object.entries(buckets).map(([key, bucket]) => {
        const averaged: RechartsDataPoint = {
            date: moment(key).format('YYYY-MM-DD'),
        };

        // Sum each numeric field across the bucket, dividing by bucket size
        for (const point of bucket) {
            for (const [k, val] of Object.entries(point)) {
                if (k === 'date' || typeof val !== 'number') continue;
                averaged[k] =
                    ((averaged[k] as number) || 0) + val / bucket.length;
            }
        }

        // Round to whole numbers for display
        for (const k of Object.keys(averaged)) {
            if (typeof averaged[k] === 'number') {
                averaged[k] = Math.round(averaged[k] as number);
            }
        }

        return averaged;
    });

    return { data: averaged, unit };
};

export const formatChartDataForRecharts = (
    data: GoogleAnalyticsData[]
): RechartsDataPoint[] => {
    const groupedByDate = data.reduce((acc, item) => {
        if (!acc[item.date]) {
            acc[item.date] = { date: item.date };
        }
        acc[item.date][item.category] = item.value;
        return acc;
    }, {} as Record<string, RechartsDataPoint>);

    const sorted = Object.values(groupedByDate).sort((a, b) => {
        const dateA = parseAnalyticsDate(a.date);
        const dateB = parseAnalyticsDate(b.date);
        return dateA.valueOf() - dateB.valueOf();
    });

    // Downsample: daily (<60d), avg per week (60-180d), avg per month (>180d)
    const { data: result, unit } = downsampleByPeriod(sorted);

    const dateFormat = unit === 'month' ? 'MMM YYYY' : 'MMM D YYYY';

    return result.map((item) => ({
        ...item,
        date: parseAnalyticsDate(item.date).format(dateFormat),
    }));
};
